import { useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { Flame } from "lucide-react";
import type { ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/i18n";

const BYPASS_PREFIXES = ["/nc/live/adm", "/login", "/api/"];

export function MaintenanceGate({ children }: { children: ReactNode }) {
  const { t } = useT();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { user } = useAuth();

  const { data } = useQuery({
    queryKey: ["site-maintenance"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "maintenance")
        .maybeSingle();
      return (data?.value ?? {}) as { enabled?: boolean; message?: string };
    },
  });

  const { data: isAdmin } = useQuery({
    queryKey: ["is-admin", user?.id ?? null],
    enabled: Boolean(user?.id) && data?.enabled === true,
    queryFn: async () => {
      const { data } = await supabase.rpc("has_role", {
        _user_id: user!.id,
        _role: "admin",
      });
      return Boolean(data);
    },
  });

  const bypass = BYPASS_PREFIXES.some((p) => pathname.startsWith(p));
  if (!data?.enabled || bypass || isAdmin) return <>{children}</>;

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <Flame className="mx-auto size-10 text-primary" />
        <h1 className="mt-4 font-display text-2xl font-bold">{t("shell.maintenance.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {data.message || t("shell.maintenance.message")}
        </p>
      </div>
    </div>
  );
}
